/* ============================================================
   pipeline.js — the kanban board's endpoints (backend/job/app.py).

   The board is one read: GET /pipeline/candidates/fetch returns inbox +
   manual_upload cards plus per-status counts. Dropping a card fires
   PATCH /candidate/stage with `inbox_id` or `manual_upload_id`.

   Inbox stage lives on inbox_messages.application_status; manual stage lives
   on manual_upload_candidate.status (same Candidate_application_Status values).
   History is application_stage_transitions in both cases.
   ============================================================ */

import { request } from '../lib/apiClient'
import { formatRole, toDate } from '../lib/format'

/**
 * Candidate_application_Status (backend/inbox/enums.py). The values ARE the
 * board stages: state, column ids, filters and the `to_stage` sent on a drop
 * all hold these values; stageLabel() is only for rendering text.
 *
 * PENDING is the backend default for a candidate not yet in the process cycle.
 * It is a status only: it is shown wherever a candidate's status is, but it is
 * not a board column (see BOARD_STAGES).
 *
 * BANKED also exists server-side but only for CV-bank rows (manual upload
 * with no job) — it is not a pipeline stage, so it is not listed here.
 */
export const APPLICATION_STATUS = Object.freeze({
  PENDING: 'PENDING',
  CLOSED: 'CLOSED',
  SHORTLIST: 'SHORTLIST',
  SCREENING: 'SCREENING',
  ASSESSMENT: 'ASSESSMENT',
  INTERVIEW: 'INTERVIEW',
  OFFER: 'OFFER',
  APPROVED: 'APPROVED',
  VERIFICATION: 'VERIFICATION',
  HIRED: 'HIRED',
  ON_HOLD: 'ON_HOLD',
  REJECTED: 'REJECTED',
})

/**
 * Board column order: the Candidate_application_Status enum sequence. Every
 * stage list on the UI (board, filters, stage dropdowns, analytics) reads this.
 * PENDING is deliberately absent: those candidates are not on the board.
 */
export const BOARD_STAGES = Object.freeze([
  APPLICATION_STATUS.CLOSED,
  APPLICATION_STATUS.SHORTLIST,
  APPLICATION_STATUS.SCREENING,
  APPLICATION_STATUS.ASSESSMENT,
  APPLICATION_STATUS.INTERVIEW,
  APPLICATION_STATUS.APPROVED,
  APPLICATION_STATUS.VERIFICATION,
  APPLICATION_STATUS.OFFER,
  APPLICATION_STATUS.HIRED,
  APPLICATION_STATUS.ON_HOLD,
  APPLICATION_STATUS.REJECTED,
])

const STATUS_VALUES = new Set(Object.values(APPLICATION_STATUS))

/** 'ON_HOLD' -> 'On Hold'. Display only — never use the result as a key. */
export function stageLabel(status) {
  return formatRole(status)
}

/** Raw API status -> an APPLICATION_STATUS value, or null if unknown/blank. */
export function toApplicationStatus(value) {
  const key = String(value ?? '').trim().toUpperCase()
  return STATUS_VALUES.has(key) ? key : null
}

/**
 * Statuses outside the Inbox Processed tab: not processed yet (PENDING, CLOSED)
 * or rejected (REJECTED). Mirrors INBOX_UNPROCESSED_STATUSES in backend/inbox/enums.py.
 */
const UNPROCESSED_STATUSES = new Set([
  APPLICATION_STATUS.PENDING,
  APPLICATION_STATUS.CLOSED,
  APPLICATION_STATUS.REJECTED,
])

/** True when the Processed tab would list this status. */
export function isProcessedStatus(value) {
  const status = toApplicationStatus(value)
  return Boolean(status) && !UNPROCESSED_STATUSES.has(status)
}

/**
 * Raw API status -> the status as the backend sent it (an APPLICATION_STATUS
 * value), or null if blank/unknown. No fallback: a card whose status is not a
 * board column (PENDING) simply has no column.
 */
export function stageOf(value) {
  return toApplicationStatus(value)
}

/**
 * Move one application to another stage. Requires pipeline.edit.
 *
 * Inbox cards send `inboxId` (INTEGER inbox.id). Manual-upload cards send
 * `manualUploadId` (manual_upload_candidate.id UUID). The server 400s if both
 * or neither are present, and 400s a no-op move ("already at stage").
 */
export function changeStage({ inboxId, manualUploadId, toStage, changeReason }) {
  return request('/candidate/stage', {
    method: 'PATCH',
    body: {
      ...(inboxId != null ? { inbox_id: inboxId } : {}),
      ...(manualUploadId != null ? { manual_upload_id: manualUploadId } : {}),
      to_stage: toStage,
      change_reason: changeReason ?? null,
    },
  })
}

/**
 * Annexure F salary slip & reference check email — POST
 * /pipeline/verification-check-email/send (pipeline.edit). The server sends it only
 * while the application is at APPROVED (409 otherwise). `deadline` is yyyy-mm-dd and
 * is not stored anywhere, so the caller must supply it; the two reference rows are
 * optional and are left blank for the candidate to fill in when omitted.
 */
export function sendVerificationCheckEmail({ inboxId, manualUploadId, deadline, hrReference, supervisorReference }) {
  return request('/pipeline/verification-check-email/send', {
    method: 'POST',
    body: {
      ...(inboxId != null ? { inbox_id: inboxId } : {}),
      ...(manualUploadId != null ? { manual_upload_id: manualUploadId } : {}),
      deadline,
      ...(hrReference ? { hr_reference: hrReference } : {}),
      ...(supervisorReference ? { supervisor_reference: supervisorReference } : {}),
    },
  })
}

/**
 * Annexure E Employment Verification Check Form preview — POST
 * /verification-check/preview (pipeline.edit). Returns the candidate user object
 * and Section 1 values. The frontend stores them and renders the document.
 */
export function previewEmploymentVerificationForm({ userId, emails }) {
  return request('/verification-check/preview', {
    method: 'POST',
    body: { user_id: userId, emails },
  })
}

/**
 * Annexure E Employment Verification Check Form — POST /verification-check/send
 * (pipeline.edit). Backend renders the PDF from the posted form values and mails
 * it. One row is recorded per referee; a prior send for the same email returns
 * status "already_sent".
 */
export function sendEmploymentVerificationForm({
  userId, emails, subject, body, candidateName, positionApplied, department, dateTime, extra,
}) {
  return request('/verification-check/send', {
    method: 'POST',
    body: {
      user_id: userId,
      emails,
      subject,
      body,
      ...(candidateName != null ? { candidate_name: candidateName } : {}),
      ...(positionApplied != null ? { position_applied: positionApplied } : {}),
      ...(department != null ? { department } : {}),
      ...(dateTime ? { date_time: dateTime } : {}),
      ...(extra || {}),
    },
  })
}

/**
 * Prior Employment Verification Check rows for one candidate — GET
 * /verification-check/fetch?candidate_id=… (candidates.view or pipeline.edit).
 * Used to reopen the form with referee emails already chosen, without asking again.
 */
export function listEmploymentVerificationForms({ candidateId } = {}) {
  return request('/verification-check/fetch', {
    params: { candidate_id: candidateId },
  })
}

/**
 * Inbox + manual-upload applications for the board — GET /pipeline/candidates/fetch
 * (pipeline.view). Envelope is `{ data: { inbox, manual_upload }, counts, total }`.
 * `jobId === ''` (All Jobs) is dropped by buildUrl and sends no filter.
 */
/** `includeClosed`: the All Jobs board hides Closed / Completed jobs unless true. */
export function listApplications({ jobId, limit, offset, search, includeClosed } = {}) {
  return request('/pipeline/candidates/fetch', {
    params: { job_post_id: jobId, limit, offset, search, include_closed: includeClosed || undefined },
  })
}

/**
 * Fold the status counts into the board columns. Statuses with no column
 * (PENDING, blank, unknown) are not counted.
 */
export function toStageCounts(byStatus) {
  const counts = Object.fromEntries(BOARD_STAGES.map((stage) => [stage, 0]))
  for (const [status, n] of Object.entries(byStatus || {})) {
    const stage = toApplicationStatus(status)
    if (stage) counts[stage] = (counts[stage] ?? 0) + (n || 0)
  }
  return counts
}

/**
 * Stage history for one application — GET /pipeline/transitions/fetch
 * (pipeline.view). Rows are valid-time intervals: `valid_to` null is the stage
 * the candidate is in now. The board itself does not render history; this is the
 * feed behind a stage timeline on the profile.
 *
 * One of inboxId / manualUploadId / transitionId is required — the route 400s
 * with none of them.
 */
export function listTransitions({ inboxId, manualUploadId, transitionId } = {}) {
  return request('/pipeline/transitions/fetch', {
    params: { inbox_id: inboxId, manual_upload_id: manualUploadId, transition_id: transitionId },
  })
}

/**
 * One candidate's CURRENT ATS score — GET /pipeline/candidate/score/fetch
 * (pipeline.view). Envelope is `{ data: { manual, inbox }, total, status_code }`,
 * where each side is `{overall_score, band, job_post_id, computed_at,
 * candidate_id, user_id}` or null.
 *
 * Score only. It carries no matched/missing keywords and no critique — those
 * live on the scored-candidate row (/candidate/scored/fetch).
 *
 * `jobPostId` pins the score to one application; omitted (as the talent pool
 * does, where a candidate need not have an assigned post) the newest current
 * score across the candidate's applications wins.
 */
export function fetchCandidateScore({ userId, jobPostId } = {}) {
  return request('/pipeline/candidate/score/fetch', {
    params: { user_id: userId, job_post_id: jobPostId },
  })
}

/**
 * The two-sided envelope -> one score row, or null.
 *
 * A candidate is reached either through the inbox (they mailed us) or through
 * Add Candidate (manual_upload); both sides resolve the same ats_results table,
 * so whichever side answered is the score. Inbox wins a tie because an emailed
 * application is the one the pipeline board is showing.
 */
export function toAtsScore(res) {
  const data = res?.data ?? {}
  return data.inbox ?? data.manual ?? null
}

/** Card fields are rendered as React children — objects throw, not just look blank. */
function asText(value, fallback = null) {
  if (value == null) return fallback
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return fallback
}

function asScore(value) {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function statusKey(value) {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && typeof value.value === 'string') return value.value
  return null
}

function sourceFields(row, kind) {
  if (kind === 'manual') {
    const id = row.id
    return {
      id: id != null ? `manual:${id}` : `manual:${row.user_id ?? row.email ?? 'unknown'}`,
      inboxId: null,
      manualUploadId: id ?? null,
      jobId: row.job_post_id ?? null,
      jobTitle: asText(row.title),
      currentTitle: asText(row.current_position),
      currentCompany: asText(row.current_company),
      source: asText(row.platform) || asText(row.apply_via) || 'Manual',
    }
  }
  return {
    id: row.inbox_id ?? `inbox:${row.user_id ?? row.email ?? 'unknown'}`,
    inboxId: row.inbox_id ?? null,
    manualUploadId: null,
    jobId: row.assigned_job_post_id ?? null,
    jobTitle: asText(row.title),
    currentTitle: asText(row.current_title),
    currentCompany: asText(row.current_employment),
    source: null,
  }
}

/**
 * Pipeline inbox or manual-upload row -> one kanban card, or null if the row
 * cannot be rendered. Inbox `id` is the inbox id, not the user id: the board
 * is one card per APPLICATION. `userId` rides along for the profile deep link.
 */
export function toBoardCard(row, kind = 'inbox') {
  if (!row || typeof row !== 'object') return null
  const src = sourceFields(row, kind)
  const status = statusKey(row.application_status)
  return {
    ...src,
    userId: row.user_id ?? null,
    name: asText(row.name) || asText(row.email) || 'Unknown',
    email: asText(row.email),
    avatarUrl: row.avatar_url || null,
    avatarViewUrl: row.avatar_view_url || null,
    stage: stageOf(status),
    status,
    experience: asText(row.experience),
    aiScore: asScore(row.ats_result?.overall_score),
    recommendation: asText(row.ats_result?.band),
    applied: toDate(row.created_at),
    isReapplicant: Boolean(row.is_reapplicant),
    previousApplications: Array.isArray(row.previous_applications) ? row.previous_applications : [],
  }
}

/** GET /pipeline/candidates/fetch `manual_upload` row -> one kanban card. */
export function toManualBoardCard(row) {
  return toBoardCard(row, 'manual')
}
